import type { PlayerKind } from '@apm/bloom-receiver-sdk';

/**
 * v36 — **개인화 채널을 잇는 엔진을 고르는 유일한 자리** (절대 규칙 93 · `docs/72` §2.1).
 *
 * `bloom` 은 지금의 리시버(더블 버퍼), `flower` 는 Flower SDK 가 채널 서버의 목록과 광고 서버의 VAST 로 HLS
 * 재생목록 하나를 조립하는 엔진이다. 화면도 하네스도 엔진을 따로 판정하지 않는다 — 이 함수의 결과를 읽는다.
 *
 * 조건은 둘의 AND 다.
 * - **hls.js 경로**(`resolvePlayerKind() === 'hls'`) — Flower 는 `XMLHttpRequest` 를 갈아 끼워 재생목록 요청을
 *   가로채는데 네이티브 HLS(`<video src>`)는 XHR 을 쓰지 않는다. 아이폰·Safari 는 그래서 언제나 `bloom` 이다(D3).
 * - **명시적 선택**(`?engine=flower`) 또는 **설치된 앱의 창**(v38 · 2026-10-22 사용자 결정) — 브라우저 탭의 기본은
 *   `bloom` 이고 선택은 저장하지 않는다(D5). 설치된 앱(노트북 Chrome · 안드로이드 홈 화면)에는 주소창이 없어 `?engine=` 을
 *   줄 길이 없고, 매니페스트 `start_url` 에 실으면 로그인·로그아웃의 `location.replace('/')` 에서 빠진다 — 그래서 주소가
 *   아니라 **문서가 설 때의 창 모양**을 입력으로 받는다. 그 창에서도 `?engine=bloom` 이면 BLOOM 이다(빠져나갈 문).
 *
 * 라이브 창은 입력이 아니다. 그것은 Flower 가 받은 재생목록에서 보고, 있으면 BLOOM 으로 폴백한다(§2.2).
 */
export type ReceiverEngine = 'bloom' | 'flower';

export type ReceiverEngineReason =
  /** 네이티브 HLS 경로(Safari·아이폰) — 무엇을 골랐든 `bloom` */
  | 'native-hls'
  /** 고르지 않았거나 `bloom` 을 골랐다 */
  | 'default'
  /** `?engine=flower` 를 hls.js 경로에서 골랐다 */
  | 'requested'
  /** v38 — 설치된 앱의 창(hls.js 경로) · 고르지 않았다 → `flower` */
  | 'installed-app'
  /** v38 — 설치된 앱의 창인데 `?engine=bloom` 을 골랐다 */
  | 'opted-out';

export interface ReceiverEngineChoice {
  engine: ReceiverEngine;
  reason: ReceiverEngineReason;
}

export function receiverEngineFor(input: {
  playerKind: PlayerKind;
  requested: ReceiverEngine | null;
  /** v38 — 문서가 설 때 설치된 앱의 창이었는가(`isAppWindow` · `config.appWindowAtLoad`) */
  appWindow: boolean;
}): ReceiverEngineChoice {
  if (input.playerKind === 'native') return { engine: 'bloom', reason: 'native-hls' };
  if (input.requested === 'flower') return { engine: 'flower', reason: 'requested' };
  if (input.appWindow) {
    return input.requested === 'bloom'
      ? { engine: 'bloom', reason: 'opted-out' }
      : { engine: 'flower', reason: 'installed-app' };
  }
  return { engine: 'bloom', reason: 'default' };
}

/**
 * `?engine=` 의 값을 엔진으로 읽는다. 모르는 값은 고르지 않은 것과 같다 — 오타로 Flower 가 켜지지 않게.
 * **저장하지 않는다**(`localStorage` 금지 · 지시서 §8) — 그 세션의 선택이다.
 */
export function parseRequestedEngine(raw: string | null): ReceiverEngine | null {
  return raw === 'flower' || raw === 'bloom' ? raw : null;
}
